import React, { useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Brush
} from "recharts";
import {
  Paper, Typography, Box, RadioGroup, FormControlLabel, Radio, Checkbox
} from "@mui/material";

const LINE_COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b",
  "#e377c2", "#7f7f7f", "#bcbd22", "#17becf", "#aec7e8", "#ffbb78",
  "#98df8a", "#ff9896", "#c5b0d5", "#c49c94"
];
const METRICS = [
  { key: "final_score",    label: "Score" },
  { key: "your_composite", label: "Abs Score" },
  { key: "ma20",           label: "M20" },
  { key: "ma50",           label: "M50" },
  { key: "r3",             label: "R3" },
  { key: "r6",             label: "R6" },
  { key: "r12",            label: "R12" },
  { key: "decision",       label: "Show Decision" }
];

// Decision -> numeric mapping for the decision view
const decisionValue = (d) => {
  if (d === "BUY") return 1;
  if (d === "HOLD") return 0;
  if (d === "SELL") return -1;
  return null;
};

export default function PortfolioChart({
  history,
  average,
  title = "Portfolio Chart",
  selectedFundIds = [],
  loading = false
}) {
  const [selectedMetric, setSelectedMetric] = useState("final_score");
  const [showAverage, setShowAverage] = useState(true);
  const isDecisionMode = selectedMetric === "decision";

  const fundList = useMemo(() => {
    if (!history || history.length === 0) return [];
    return history[0].funds.map(f => ({ id: f.fund_id, name: f.name }));
  }, [history]);

  const getFundColor = (fundId) => {
    const idx = fundList.findIndex(f => f.id === fundId);
    return LINE_COLORS[idx % LINE_COLORS.length];
  };

  const chartData = useMemo(() => {
    if (!history || history.length === 0) return [];
    return history.map(row => {
      const point = { date: row.date };
      const allDecisions = [];   // averaged over ALL funds in this table row
      (row.funds || []).forEach(fund => {
        const dv = decisionValue(fund.final_decision);
        // Average always accumulates from every fund, selection-independent
        if (dv !== null) allDecisions.push(dv);
        // Per-fund values only for the selected funds
        if (selectedFundIds.includes(fund.fund_id)) {
          point[`name_${fund.fund_id}`] = fund.name;
          point[`dec_${fund.fund_id}`] = dv;
          if (!isDecisionMode) {
            point[`fund_${fund.fund_id}`] = fund[selectedMetric];
          }
        }
      });
      if (!isDecisionMode) {
        const av = (average || []).find(a => a.date === row.date);
        point.average = av ? av[selectedMetric] : null;
      }
      point.avg_decision = allDecisions.length
        ? allDecisions.reduce((a, b) => a + b, 0) / allDecisions.length
        : null;
      return point;
    });
  }, [history, average, selectedMetric, selectedFundIds, isDecisionMode]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <Paper sx={{ p: 1 }}>
        <Typography variant="subtitle2">{label}</Typography>
        {payload.map((entry, idx) => {
          const dataKey = String(entry.dataKey);
          const isPerFundDecision = dataKey.startsWith("dec_");
          let displayName;
          if (dataKey === "average") displayName = "Average";
          else if (dataKey === "avg_decision") displayName = "Avg Decision";
          else if (isPerFundDecision) {
            const id = dataKey.replace("dec_", "");
            displayName = (entry.payload[`name_${id}`] || id) + " (Decision)";
          } else {
            const id = dataKey.replace("fund_", "");
            displayName = entry.payload[`name_${id}`] || dataKey;
          }
          let val = "n/a";
          if (entry.value !== null && entry.value !== undefined) {
            val = isPerFundDecision
              ? (entry.value === 1 ? "BUY"
                 : entry.value === -1 ? "SELL"
                 : entry.value === 0 ? "HOLD"
                 : String(Number(entry.value).toFixed(2)))
              : Number(entry.value).toFixed(2);
          }
          return (
            <Typography key={idx} variant="caption" sx={{ color: entry.color, display: "block" }}>
              {displayName}: {val}
            </Typography>
          );
        })}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography sx={{ p: 2, color: "text.secondary" }}>
          Loading chart data…
        </Typography>
      </Paper>
    );
  }

  if (!history || history.length === 0) {
      return (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6">{title}</Typography>
          <Typography sx={{ p: 2, color: "text.secondary" }}>
            No portfolio chart data available.
          </Typography>
        </Paper>
      );
    }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: 1
      }}>
        <Typography variant="h6">{title}</Typography>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={showAverage}
              onChange={(e) => setShowAverage(e.target.checked)} />
          }
          label="Show Average"
        />
      </Box>

      <Box sx={{ mt: 2, mb: 2 }}>
        <RadioGroup row value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
          {METRICS.map(m => (
            <FormControlLabel key={m.key} value={m.key} control={<Radio size="small" />} label={m.label} />
          ))}
        </RadioGroup>
      </Box>

      <Box sx={{ width: "100%", height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" padding={{ left: 0, right: 0 }} />
            <YAxis domain={isDecisionMode ? [-1.2, 1.2] : ["auto", "auto"]} />
            <Tooltip content={<CustomTooltip />} />

            {fundList.map(fund => {
              if (!selectedFundIds.includes(fund.id)) return null;
              if (isDecisionMode) {
                return (
                  <Line
                    key={`dec_${fund.id}`}
                    type="stepAfter"
                    dataKey={`dec_${fund.id}`}
                    stroke={getFundColor(fund.id)}
                    strokeWidth={1.2}
                    strokeDasharray="4 3"
                    dot={false}
                    opacity={0.7}
                  />
                );
              }
              return (
                <Line
                  key={fund.id}
                  type="monotone"
                  dataKey={`fund_${fund.id}`}
                  stroke={getFundColor(fund.id)}
                  strokeWidth={1.5}
                  dot={false}
                />
              );
            })}

            {showAverage && (
              isDecisionMode ? (
                <Line
                  type="stepAfter"
                  dataKey="avg_decision"
                  stroke="#000000"
                  strokeWidth={2.5}
                  dot={false}
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="average"
                  stroke="#1f77ff"
                  strokeWidth={3}
                  dot={false}
                />
              )
            )}


            <Brush
              dataKey="date"
              height={30}
              stroke="#1f77ff"
              travellerWidth={12}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
